import { create } from 'zustand';
import { setUserBranchesCache } from '../../../auth/infra/branches/branchesMemoryCache';
import { cacheBranches, clearAllCache, getCachedBranches, type BranchCacheEntry } from '../../../auth/infra/branches/branchesCache';
import { serializeBranchPermissions, parseBranchPermissionsJson } from '../../../auth/infra/branches/branchPermissionsCache';
import { setSentryBranchTag } from '../../../shared/sentry/sentryUser';
import {
  applyBranchChangeSideEffects,
  resolveBranchIdAfterListUpdate,
  shouldRunBranchChangeSideEffects,
} from '../../../auth/store/branchChange';
import { setConsolidateCartLines } from '../../../shared/pos/posCartSettings';

export type { ModuleKey, BranchPermission, BranchBarcodeInputMode, BranchScaleBarcodeProfile, BranchAccess } from './sessionTypes';
import type { ModuleKey, BranchPermission, BranchAccess } from './sessionTypes';

// Throttling para cacheBranches
let cacheInProgress = false;
let cacheScheduled = false;
const CACHE_THROTTLE_MS = 1000;

interface SessionState {
  currentBranchId?: string;
  branches: BranchAccess[];
  branchesLoading: boolean;
  setCurrentBranch: (branchId?: string, options?: { resetShell?: boolean }) => void;
  setBranches: (branches: BranchAccess[]) => void;
  setBranchesLoading: (loading: boolean) => void;
  resetSession: () => void;
  loadBranchesFromCache: () => Promise<void>;
}

export const defaultPermissions: BranchPermission[] = [
  { module: 'POS', canView: true, canEdit: true, canVoid: true, canDiscount: true, canViewOthers: true, actions: {} },
  { module: 'Catalog', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Inventory', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Customers', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Reports', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'BranchSettings', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Promotions', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'PriceLists', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Loyalty', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Expenses', canView: true, canEdit: true, canVoid: true, canViewOthers: true, actions: {} },
  { module: 'Purchases', canView: true, canEdit: true, canVoid: true, canViewOthers: true, actions: { receive: true, return: true, view_cost: true, export: true } },
  { module: 'Agenda', canView: true, canEdit: true, canManageAgenda: true, canViewOthers: true, actions: {} },
  { module: 'WorkOrders', canView: true, canEdit: true, canVoid: true, canViewOthers: true, actions: {} },
  { module: 'Commissions', canView: true, canEdit: true, canVoid: true, canViewOthers: true, actions: {} },
  { module: 'Cash', canView: true, canEdit: true, canVoid: true, canViewOthers: true, actions: {} },
  { module: 'Checks', canView: true, canEdit: true, canViewOthers: true, actions: { endorse: true, reject: true } },
  { module: 'Orders', canView: true, canEdit: true, canVoid: true, canViewOthers: true, actions: {} },
  { module: 'Tables', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Kitchen', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'MenuOptions', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Team', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Organization', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Billing', canView: true, canEdit: true, canViewOthers: true, actions: {} },
  { module: 'Support', canView: true, canEdit: true, canViewOthers: true, actions: {} },
];

export const emptyState = {
  branches: [] as BranchAccess[],
  currentBranchId: undefined as string | undefined,
  branchesLoading: false,
};

/** Sincroniza el setting de consolidate_cart_lines de la sucursal activa al posCartSettings. */
function syncConsolidateCartLines(
  branches: BranchAccess[],
  currentBranchId: string | undefined,
): void {
  const branch = branches.find((b) => b.id === currentBranchId);
  setConsolidateCartLines(branch?.consolidateCartLines ?? true);
}

export const useSessionStore = create<SessionState>((set) => ({
  ...emptyState,
  setCurrentBranch: (branchId?: string, options?: { resetShell?: boolean }) => {
    const prev = useSessionStore.getState().currentBranchId;
    set({ currentBranchId: branchId });
    setSentryBranchTag(branchId ?? null);
    syncConsolidateCartLines(useSessionStore.getState().branches, branchId);
    if (shouldRunBranchChangeSideEffects(prev, branchId)) {
      applyBranchChangeSideEffects({ resetShell: options?.resetShell !== false });
    }
  },
  setBranches: (branches: BranchAccess[]) => {
    const prev = useSessionStore.getState().currentBranchId;
    const currentBranchId = resolveBranchIdAfterListUpdate(prev, branches);
    setUserBranchesCache(branches);

    set(() => {
      // Guardar sucursales en caché local para uso offline (con throttling)
      const cacheEntries: BranchCacheEntry[] = branches.map((branch) => ({
        id: branch.id,
        name: branch.name,
        address: branch.location,
        stock_sale_policy: branch.stockSalePolicy,
        barcode_input_mode: branch.barcodeInputMode === 'hardware' ? 'hardware' : branch.barcodeInputMode === 'camera' ? 'camera' : 'both',
        scale_barcode_profile: branch.scaleBarcodeProfile ?? 'gs1_prefix',
        manual_discount_max_percent: branch.manualDiscountMaxPercent ?? 20,
        manual_discount_max_amount: branch.manualDiscountMaxAmount ?? 5000,
        consolidate_cart_lines: branch.consolidateCartLines !== false,
        kitchen_fire_mode: branch.kitchenFireMode === 'before_payment' ? 'before_payment' : 'after_payment',
        cached_at: Date.now(),
        permissions_json: serializeBranchPermissions(branch.permissions),
      }));

      if (!cacheInProgress && !cacheScheduled) {
        cacheScheduled = true;
        setTimeout(async () => {
          cacheInProgress = true;
          cacheScheduled = false;
          try {
            await cacheBranches(cacheEntries);
          } finally {
            cacheInProgress = false;
          }
        }, CACHE_THROTTLE_MS);
      }

      return { branches, currentBranchId, branchesLoading: false };
    });

    syncConsolidateCartLines(branches, currentBranchId);

    if (shouldRunBranchChangeSideEffects(prev, currentBranchId)) {
      applyBranchChangeSideEffects();
    }
  },
  setBranchesLoading: (loading: boolean) => set({ branchesLoading: loading }),
  resetSession: () => set({ ...emptyState }),
  loadBranchesFromCache: async () => {
    try {
      const cachedBranches = await getCachedBranches();
      if (cachedBranches.length > 0) {
        const branches: BranchAccess[] = cachedBranches.map((branch) => {
          const cachedPermissions = parseBranchPermissionsJson(branch.permissions_json);
          return {
            id: branch.id,
            name: branch.name,
            location: branch.address ?? 'Sin ubicación',
            permissions: cachedPermissions ?? [],
            permissionsOfflineUnknown: cachedPermissions == null,
            stockSalePolicy: branch.stock_sale_policy,
            barcodeInputMode: branch.barcode_input_mode,
            scaleBarcodeProfile: branch.scale_barcode_profile ?? 'gs1_prefix',
            manualDiscountMaxPercent: branch.manual_discount_max_percent ?? 20,
            manualDiscountMaxAmount: branch.manual_discount_max_amount ?? 5000,
            consolidateCartLines: branch.consolidate_cart_lines !== false,
            kitchenFireMode: branch.kitchen_fire_mode === 'before_payment' ? 'before_payment' : 'after_payment',
          };
        });
        const prev = useSessionStore.getState().currentBranchId;
        const currentBranchId = resolveBranchIdAfterListUpdate(prev, branches);
        set({
          branches,
          currentBranchId,
          branchesLoading: false,
        });
        syncConsolidateCartLines(branches, currentBranchId);
        if (shouldRunBranchChangeSideEffects(prev, currentBranchId)) {
          applyBranchChangeSideEffects();
        }
      }
    } catch (error) {
      console.warn('[useSessionStore] Error al cargar branches desde caché:', error);
    }
  },
}));
